/* eslint-disable max-lines */
import { SYSTEMS, SYSTEMS_WITH_USER_PROPERTIES } from './constants';

import Utils from './helpers/Utils';

import { UAParser } from 'ua-parser-js';
import { Types as AmplitudeTypes } from '@amplitude/analytics-browser';
import AmplitudeAnalytic from './Systems/Amplitude';
import AwsAnalytic from './Systems/Amazon';
import { AnalyticsType, IInitSystems, SendAllOptions, SendOptions, ISystemParams } from './Interfaces/Systems';
import { ITrackNew } from './Interfaces/Events';
import DataProvider from './DataProvider';

interface SendFunction {
    (system: AnalyticsType): void;
}

class Analytic {
    private state: {
        activeSystems: AnalyticsType[];
        isInit: boolean;
        allInitialized: boolean;
    };

    constructor() {
        this.state = {
            activeSystems: [],
            isInit: false,
            allInitialized: false,
        };
    }

    initSystems(listOfSystems: IInitSystems) {
        const options = { event: 'Init system' };

        listOfSystems.activeSystems.forEach((system: ISystemParams) => {
            this.send(system, options, () => {
                const checkSystemsStatus: boolean = this.checkIsAlreadyInited(system);

                if (!checkSystemsStatus) {
                    const analyticInstance = new SYSTEMS[system.name]();

                    analyticInstance.init(
                        Utils.cleanObject({
                            id: system.id,
                            config: system?.config,
                            email: system?.email,
                        })
                    );

                    this.state.activeSystems.push(analyticInstance);

                    console.log(`%cinit - ${analyticInstance.getName()}`, 'color:green;'); // eslint-disable-line no-console
                }
            });
            if (listOfSystems.activeSystems.length === this.state.activeSystems.length) {
                console.log('All systems are initialized'); // eslint-disable-line no-console

                this.state.allInitialized = true;
            }
        });
    }

    send(
        system: ISystemParams | AnalyticsType,
        options: SendOptions,
        callback: (system?: ISystemParams | AnalyticsType) => void
    ): void {
        try {
            callback(system);
        } catch (error) {
            const defaultOptions = {
                systemName: system.name,
                error: error,
            };

            Utils.errorConsoleGroup({ ...defaultOptions, ...options });
        }
    }

    createCustomSystems(customSystems: string[]) {
        return customSystems.map((el: string) => {
            return this.state.activeSystems.find((item: AnalyticsType) => item.getName() === el);
        });
    }

    sendAll(options: SendAllOptions, callback: SendFunction) {
        const customSystems = options?.customSystems?.length && this.createCustomSystems(options.customSystems);
        const activeSystems = this.state.activeSystems;

        const systems = customSystems || activeSystems;

        systems.forEach((system?: AnalyticsType) => {
            if (system) {
                // @ts-ignore
                this.send(system, options, callback);
            }
        });
    }

    getSystemsState() {
        return this.state.allInitialized;
    }

    setUserProperties(data: Record<string, AmplitudeTypes.ValidPropertyType | null | undefined>) {
        this.sendAll(
            {
                event: 'setUserProperties',
                customSystems: SYSTEMS_WITH_USER_PROPERTIES,
            },
            (system: AnalyticsType) => {
                !(system instanceof AmplitudeAnalytic) || system?.setUserProperties(data);
            }
        );
    }

    trackNew({ eventData, actionData = null, data = null }: ITrackNew) {
        const event = typeof eventData === 'object' ? `${eventData.screenId}__${eventData.event}` : eventData;

        let action: string | null = null;

        if (typeof actionData === 'object') {
            if (actionData?.elementEvent && actionData?.actionEvent) {
                action = `${actionData.elementEvent}__${actionData.actionEvent}`;
            }
        }

        // Fallback logic to use custom eventAction
        if (typeof actionData === 'string') {
            action = actionData;
        }

        const options = {
            event: event,
            data: Utils.normalizeEventLabelFormat(data),
        };

        this.sendAll(options, (system: AnalyticsType) => {
            system.trackNew({
                eventName: Utils.normalizeLabel(event),
                eventAction: action ? Utils.normalizeLabel(action) : null,
                eventData: Utils.normalizeEventLabelFormat(data),
            });
        });
    }

    checkIsAlreadyInited(checkSystem: AnalyticsType | { name: string }): boolean {
        let status = false;

        this.state.activeSystems.forEach((system: AnalyticsType) => {
            if (system.getName() === checkSystem.name) {
                console.warn(`Already inited system - ${checkSystem.name}`);
                status = true;
            }
        });

        return status;
    }

    getDefaultAmazonAnalyticPurchaseData() {
        const awsSystem = this.state.activeSystems.find((item: AnalyticsType) => item.name === 'amazon');

        return awsSystem instanceof AwsAnalytic ? awsSystem?.getDefaultRecordData() : null;
    }
}

export const Provider = DataProvider;
export const Main = new Analytic();
export const uaParser = UAParser;
export { SYSTEMS_NAMES } from './constants';
export type { AnalyticsType, IInitSystems, SendAllOptions, SendOptions, ISystemParams } from './Interfaces/Systems';
export type {
    ITrackNew,
    IEventAction,
    IEventTrigger,
    ITrackNewAnalytics,
    PurchaseData,
    EventDataTrack,
    EventDataOverrides,
} from './Interfaces/Events';
export { ActionEventAction, ElementEventAction, EventTrigger, EventTriggerFixed } from './Interfaces/Events';
